/**
 * Manual payroll inputs for one run — roadmap 2026-09-10 A-6 / B3, the first half of
 * E5-4. `web/shapes/payroll.md` §7a is the agreed shape.
 *
 * The Calculate button used to send `{}`: Lương CB, tỷ lệ đạt, phụ cấp, thưởng, tạm ứng
 * and the dependant count were zero on every payslip calculated from the web (B3). A
 * run's Calculate control now opens this panel. It holds:
 *
 *  - one row per employee the run pays (`GET /payroll/runs/{id}/roster`, the same
 *    contracts the calculation iterates);
 *  - a cell per manual field, and a CSV box;
 *  - the press that sends them.
 *
 * Its rules:
 *
 *  - **Only what a person typed travels** — `payroll/employee-inputs.ts`'s header.
 *    Nothing is pre-filled, not even the last calculation's inputs. A recalculation
 *    replaces every manual input with exactly what this grid sends, and the panel says so
 *    before the press rather than resending numbers nobody looked at.
 *  - **An invalid cell says why, in place, and the Calculate control is absent** until it
 *    is fixed — the codebase's absent-not-disabled rule, with the reason beside it.
 *  - **Nothing is kept in browser storage.** The grid lives in `PayrollScreen`'s state
 *    while the screen is open; salary figures do not go to `localStorage`.
 *  - **A refusal is the server's own sentence**, here where the press was, not on the
 *    run's row — and the `409`'s `details.reason` beside it, verbatim, labelled as the
 *    server's words (`refusalReasonOf`, the confirmation form's precedent).
 *  - **The outcome outlives the reload** (fix round 1, R20). After that `409` the runs
 *    re-read shows the run approved, so `calculable` turns false; `RunsPanel` keeps this
 *    panel while it holds the run's outcome. The grid and Calculate are absent then, a
 *    sentence says why, and the refusal — or the landed count — stays.
 *  - **One run write at a time** (R21). While any run write is in flight (`pendingRunId`,
 *    a calculation or an approval, this run or another), Calculate, Close and Clear are
 *    absent and the pending line names the run being waited for. Close would sever the
 *    write from its outcome, Clear would drop the grid a refusal sends the person back
 *    to, and a second press would race it on one shared mutation.
 */

import { useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import type { PayrollCalculateRunRequest, PayrollRosterEntry } from '../../api/payroll';
import {
  calculateValidationIssueOf,
  deductionCapExceededDetailsOf,
  deductionExceedsNetPayDetailsOf,
  reductionExceedsBonusDetailsOf,
  refusalReasonOf,
} from '../../api/payroll';
import type { QueryStatus } from '../../api/useApiQuery';
import type { WorkforceApiError } from '../../api/errors';
import { useCompanyCurrency } from '../../api/company-settings';
import { formatMoney } from '../../payroll/status';
import {
  ACCEPTED_CSV_COLUMNS,
  CELL_PROBLEM_LABEL,
  CSV_ISSUE_LABEL,
  MAX_EMPLOYEE_INPUTS,
  PAYROLL_INPUT_BOUNDS,
  PAYROLL_INPUT_FIELDS,
  PAYROLL_INPUT_FORMAT,
  PAYROLL_INPUT_HINT,
  PAYROLL_INPUT_LABEL,
  PENALTY_PROBLEM_LABEL,
  PENALTY_REASON_MIN,
  applyInputCsv,
  employeeInputsOf,
  gridProblems,
  gridSummary,
  readInputCell,
  readPenaltyCells,
  setGridCell,
  type CsvIssue,
  type InputGrid,
} from '../../payroll/employee-inputs';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { EmptyState } from '../../states/EmptyState';
import { Skeleton } from '../../states/Skeleton';
import { StatusNote } from '../../states/StatusNote';

export interface RunInputsPanelProps {
  readonly runId: string;
  /**
   * The run can still be calculated (`draft`/`calculated`). `false` once a re-read shows
   * it approved: the panel then shows only its outcome — no grid, no Calculate (R20).
   */
  readonly calculable: boolean;
  readonly rosterStatus: QueryStatus;
  /** `GET /payroll/runs/{id}/roster`'s `data`, or `null` until it has answered. */
  readonly roster: readonly PayrollRosterEntry[] | null;
  readonly rosterError: WorkforceApiError | null;
  readonly onReloadRoster: () => void;
  /** This run's grid, as typed so far. */
  readonly grid: InputGrid;
  readonly onGridChange: (grid: InputGrid) => void;
  /**
   * The run whose write — a calculation or an approval — is in flight, or `null`. Any run,
   * not only this one: one run write at a time (R21).
   */
  readonly pendingRunId: string | null;
  /** The refusal of the last calculation sent for THIS run, or `null`. */
  readonly calculateError: WorkforceApiError | null;
  /** How many payslips the last calculation of THIS run wrote; `null` until one landed. */
  readonly calculatedCount: number | null;
  readonly onCalculate: (body: PayrollCalculateRunRequest) => void;
  readonly onClose: () => void;
}

interface CsvOutcome {
  readonly appliedRows: number;
  readonly issues: readonly CsvIssue[];
}

/** A chosen file's text. `FileReader` rather than `Blob.text()`, which jsdom 26 lacks. */
function readTextFile(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(typeof reader.result === 'string' ? reader.result : '');
    };
    reader.onerror = () => {
      reject(reader.error ?? new Error('the file could not be read'));
    };
    reader.readAsText(file);
  });
}

export function RunInputsPanel(props: RunInputsPanelProps): ReactNode {
  const { t } = useI18n();
  // The company's own unit (Task CS-2). The three refusals below quote figures the
  // server compared, and a figure quoted in the wrong unit is worse than none.
  const currency = useCompanyCurrency();
  const [csvText, setCsvText] = useState('');
  const [csvOutcome, setCsvOutcome] = useState<CsvOutcome | null>(null);
  const [fileUnreadable, setFileUnreadable] = useState(false);

  const roster = props.roster ?? [];
  const rosterIds = roster.map((entry) => entry.employee_id);
  const ready = props.rosterStatus === 'ok' && props.roster !== null;
  const problems = gridProblems(props.grid, rosterIds);
  const summary = gridSummary(props.grid, rosterIds);
  const tooMany = summary.employeesWithInputs > MAX_EMPLOYEE_INPUTS;
  /** The grid is shown and can be sent: the run is calculable and its roster is in. */
  const editable = props.calculable && ready;
  /** Any run write in flight — this panel then offers no press at all (R21). */
  const writePending = props.pendingRunId !== null;
  const canCalculate = editable && problems.length === 0 && !tooMany && !writePending;
  const anythingTyped = summary.enteredCells > 0 || problems.length > 0;
  /** Why Calculate is absent, said where it would be; `null` when nothing needs saying. */
  const blockedReason =
    props.pendingRunId !== null
      ? t('payroll.inputs.blocked.pending', { id: props.pendingRunId })
      : !editable
        ? null
        : problems.length > 0
          ? t('payroll.inputs.blocked.problems', { count: problems.length })
          : tooMany
            ? t('payroll.inputs.blocked.tooMany', { max: MAX_EMPLOYEE_INPUTS })
            : null;
  // `', '`, not `','`: joined without spaces the list is one unbreakable word wider than a
  // tablet row. The parser trims header cells, so the spaced list still pastes as a header.
  const columns = ACCEPTED_CSV_COLUMNS.join(', ');
  /*
    `details.reason` is read off the calculate `409` and nothing else: there it is the
    rule that fired ("a run with any approval can no longer be recomputed"). Any other
    refusal's details are not a sentence meant for this line.
  */
  const calculateReason =
    props.calculateError !== null &&
    props.calculateError.status === 409 &&
    props.calculateError.code === 'WORKFORCE_INVALID_STATE_TRANSITION'
      ? refusalReasonOf(props.calculateError)
      : null;
  /*
    Fix round 1 I5: a capped ground's deduction over the ceiling carries the employee,
    the amount and the ceiling in `details` — printed here, not left to the generic
    `ApiErrorNote` sentence, so a clerk on a multi-hundred-person run is told WHOSE
    deduction and by how much rather than a correlation id to search a spreadsheet by.
  */
  const capExceeded =
    props.calculateError !== null &&
    props.calculateError.code === 'WORKFORCE_DEDUCTION_CAP_EXCEEDED'
      ? deductionCapExceededDetailsOf(props.calculateError)
      : null;
  /** Fix round 1 C1: the sibling refusal for a deduction that would take net pay below zero. */
  const netPayExceeded =
    props.calculateError !== null && props.calculateError.code === 'WORKFORCE_VALIDATION_FAILED'
      ? deductionExceedsNetPayDetailsOf(props.calculateError)
      : null;
  /**
   * I-5: the gross-side sibling of `netPayExceeded` — a `phat` on a `gross_before_tax`
   * ground that exceeds the bonus it reduces. The engine no longer clamps this to the
   * bonus (`Math.min`); the service refuses instead, and this reads that refusal.
   */
  const bonusExceeded =
    props.calculateError !== null &&
    props.calculateError.code === 'WORKFORCE_VALIDATION_FAILED' &&
    netPayExceeded === null
      ? reductionExceedsBonusDetailsOf(props.calculateError)
      : null;
  /**
   * Fix round 1 I5/I6: the calculate body's own validation issues — an amount with no
   * basis (schema-level, no employee named) or a `phat_can_cu` naming no ground this
   * formula version configured (calculate-time, employee named). Read only when
   * neither `netPayExceeded` nor `bonusExceeded` already claimed this same error code,
   * since all three are `WORKFORCE_VALIDATION_FAILED` with different `details` shapes.
   */
  const calculateIssue =
    props.calculateError !== null &&
    props.calculateError.code === 'WORKFORCE_VALIDATION_FAILED' &&
    netPayExceeded === null &&
    bonusExceeded === null
      ? calculateValidationIssueOf(props.calculateError)
      : null;

  const applyCsv = (): void => {
    const outcome = applyInputCsv(props.grid, csvText, rosterIds);
    setCsvOutcome({ appliedRows: outcome.appliedRows, issues: outcome.issues });
    props.onGridChange(outcome.grid);
  };

  const calculateNow = (): void => {
    const inputs = employeeInputsOf(props.grid, rosterIds);
    // Nothing typed is `{}` — what the server defaults — never `{ employee_inputs: [] }`.
    props.onCalculate(inputs.length === 0 ? {} : { employee_inputs: inputs });
  };

  return (
    // `wp-panel`: its `min-width: 0` is what lets this full-width child of the run's
    // `wp-row` (a wrapping flex row) shrink to the row, so the grid scrolls inside
    // `wp-table-scroll` instead of pushing the whole page sideways.
    <div className="wp-panel" data-testid="w9-inputs">
      <h4 className="wp-section__title">{t('payroll.inputs.title', { id: props.runId })}</h4>
      {props.calculable ? null : (
        <p className="wp-small" data-testid="w9-inputs-not-calculable">
          {t('payroll.inputs.notCalculable')}
        </p>
      )}
      {props.calculable ? (
        <>
          <p className="wp-small wp-muted">{t('payroll.inputs.intro')}</p>
          <p className="wp-small" data-testid="w9-inputs-zero-rule">
            {t('payroll.inputs.zeroRule')}
          </p>
          <p className="wp-small wp-muted" data-testid="w9-inputs-no-phat">
            {t('payroll.inputs.phatLawful')}
          </p>

          {props.rosterStatus === 'loading' ? (
            <Skeleton
              rows={3}
              variant="block"
              label={t('payroll.inputs.roster.subject')}
              testId="w9-inputs-roster-skeleton"
            />
          ) : null}
          {props.rosterStatus === 'error' && props.rosterError !== null ? (
            <ApiErrorNote
              error={props.rosterError}
              onRetry={props.onReloadRoster}
              subject={t('payroll.inputs.roster.subject')}
              testId="w9-inputs-roster-error"
            />
          ) : null}
          {ready && roster.length === 0 ? (
            <EmptyState
              title={t('payroll.inputs.roster.empty.title')}
              description={t('payroll.inputs.roster.empty.body')}
              testId="w9-inputs-roster-empty"
            />
          ) : null}

          {ready && roster.length > 0 ? (
            <>
              <div className="wp-section__subsection" data-testid="w9-inputs-csv-block">
                <h5 className="wp-section__title">{t('payroll.inputs.csv.title')}</h5>
                <p className="wp-small wp-muted">{t('payroll.inputs.csv.hint', { columns })}</p>
                <div className="wp-field">
                  <label className="wp-field__label" htmlFor="w9-inputs-csv">
                    {t('payroll.inputs.csv.paste')}
                  </label>
                  <textarea
                    className="wp-field__input"
                    id="w9-inputs-csv"
                    data-testid="w9-inputs-csv"
                    value={csvText}
                    onChange={(event) => {
                      setCsvText(event.target.value);
                    }}
                  />
                </div>
                <div className="wp-field">
                  <label className="wp-field__label" htmlFor="w9-inputs-csv-file">
                    {t('payroll.inputs.csv.file')}
                  </label>
                  <input
                    className="wp-field__input"
                    id="w9-inputs-csv-file"
                    data-testid="w9-inputs-csv-file"
                    type="file"
                    accept=".csv,.tsv,.txt,text/csv,text/tab-separated-values,text/plain"
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      if (file === undefined) return;
                      setFileUnreadable(false);
                      readTextFile(file)
                        .then(setCsvText)
                        .catch(() => {
                          setFileUnreadable(true);
                        });
                    }}
                  />
                  {fileUnreadable ? (
                    <p className="wp-small" role="alert" data-testid="w9-inputs-csv-file-error">
                      {t('payroll.inputs.csv.fileError')}
                    </p>
                  ) : null}
                </div>
                {/* Absent until there is something to apply — never a dead button. */}
                {csvText.trim() === '' ? null : (
                  <div className="wp-filters__actions">
                    <button
                      className="wp-button"
                      type="button"
                      data-testid="w9-inputs-csv-apply"
                      onClick={applyCsv}
                    >
                      {t('payroll.inputs.csv.apply')}
                    </button>
                  </div>
                )}
                {csvOutcome === null ? null : csvOutcome.issues.length === 0 ? (
                  <StatusNote
                    severity="low"
                    title={t('payroll.inputs.csv.applied', { rows: csvOutcome.appliedRows })}
                    testId="w9-inputs-csv-applied"
                  />
                ) : (
                  <StatusNote
                    severity="medium"
                    role="alert"
                    title={t('payroll.inputs.csv.issues', { rows: csvOutcome.appliedRows })}
                    testId="w9-inputs-csv-issues"
                  >
                    <ul>
                      {csvOutcome.issues.map((issue, index) => (
                        <li
                          className="wp-small"
                          key={`${String(issue.line)}-${issue.kind}-${String(index)}`}
                          data-testid={`w9-inputs-csv-issue-${String(index)}`}
                        >
                          {t(CSV_ISSUE_LABEL[issue.kind], {
                            line: issue.line,
                            detail: issue.detail,
                            columns,
                            max: MAX_EMPLOYEE_INPUTS,
                          })}
                        </li>
                      ))}
                    </ul>
                  </StatusNote>
                )}
              </div>

              <div className="wp-table-scroll">
                <table className="wp-table" data-testid="w9-inputs-grid">
                  <caption className="wp-visually-hidden">
                    {t('payroll.inputs.grid.caption')}
                  </caption>
                  <thead>
                    <tr>
                      <th scope="col">{t('payroll.inputs.grid.employee')}</th>
                      {PAYROLL_INPUT_FIELDS.map((field) => (
                        <th scope="col" className="wp-num" key={field}>
                          {t(PAYROLL_INPUT_LABEL[field])}
                          <br />
                          <span className="wp-small wp-muted">
                            {t(PAYROLL_INPUT_HINT[field], {
                              max: PAYROLL_INPUT_BOUNDS[field].max ?? '',
                              currency,
                            })}
                          </span>
                        </th>
                      ))}
                      <th scope="col">
                        {t('payroll.inputs.penalty.basis.label')}
                        <br />
                        <span className="wp-small wp-muted">
                          {t('payroll.inputs.penalty.basis.hint')}
                        </span>
                      </th>
                      <th scope="col">
                        {t('payroll.inputs.penalty.reason.label')}
                        <br />
                        <span className="wp-small wp-muted">
                          {t('payroll.inputs.penalty.reason.hint', { min: PENALTY_REASON_MIN })}
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {roster.map((entry) => {
                      const who = entry.full_name ?? t('payroll.inputs.grid.unnamed');
                      // The two penalty cells travel beside `phat` on the SAME row: the
                      // basis and the reason are what make that row's amount lawful.
                      const amountRaw = props.grid[entry.employee_id]?.phat ?? '';
                      const basisRaw = props.grid[entry.employee_id]?.phat_can_cu ?? '';
                      const reasonRaw = props.grid[entry.employee_id]?.phat_ly_do ?? '';
                      const penaltyProblems = readPenaltyCells(amountRaw, basisRaw, reasonRaw);
                      const basisProblem =
                        penaltyProblems.find((problem) => problem === 'basis_required') ?? null;
                      const reasonProblem =
                        penaltyProblems.find(
                          (problem) =>
                            problem === 'reason_required' || problem === 'reason_too_short',
                        ) ?? null;
                      const basisCellId = `w9-input-${entry.employee_id}-phat_can_cu`;
                      const reasonCellId = `w9-input-${entry.employee_id}-phat_ly_do`;
                      return (
                        <tr
                          key={entry.employee_id}
                          data-testid={`w9-inputs-row-${entry.employee_id}`}
                        >
                          <th scope="row">
                            {who} <span className="wp-small wp-muted">#{entry.employee_id}</span>
                          </th>
                          {PAYROLL_INPUT_FIELDS.map((field) => {
                            const raw = props.grid[entry.employee_id]?.[field] ?? '';
                            const { problem } = readInputCell(field, raw);
                            const cellId = `w9-input-${entry.employee_id}-${field}`;
                            return (
                              <td className="wp-num" key={field}>
                                <input
                                  className="wp-field__input"
                                  id={cellId}
                                  data-testid={cellId}
                                  type="text"
                                  /* An amount is whole đồng, so its keypad has no
                                     decimal point to offer (R19, final review R25); tỷ
                                     lệ đạt and the count keep one that does. */
                                  inputMode={
                                    PAYROLL_INPUT_FORMAT[field] === 'dong' ? 'numeric' : 'decimal'
                                  }
                                  autoComplete="off"
                                  aria-label={t('payroll.inputs.grid.cellLabel', {
                                    field: t(PAYROLL_INPUT_LABEL[field]),
                                    employee: who,
                                  })}
                                  aria-invalid={problem !== null}
                                  {...(problem === null
                                    ? {}
                                    : { 'aria-describedby': `${cellId}-problem` })}
                                  value={raw}
                                  onChange={(event) => {
                                    props.onGridChange(
                                      setGridCell(
                                        props.grid,
                                        entry.employee_id,
                                        field,
                                        event.target.value,
                                      ),
                                    );
                                  }}
                                />
                                {problem === null ? null : (
                                  <p
                                    className="wp-small"
                                    id={`${cellId}-problem`}
                                    data-testid={`w9-input-problem-${entry.employee_id}-${field}`}
                                  >
                                    {t(CELL_PROBLEM_LABEL[problem], {
                                      min: PAYROLL_INPUT_BOUNDS[field].min,
                                      max: PAYROLL_INPUT_BOUNDS[field].max ?? '',
                                    })}
                                  </p>
                                )}
                              </td>
                            );
                          })}
                          <td>
                            {/*
                              A text cell, not a `<select>`: the lawful grounds are this
                              company's OWN configured `deduction_grounds` on its active
                              formula version, which this panel does not fetch, so there
                              is no fixed list to offer as options. A ground this
                              formula version did not configure is refused by the
                              server, by field, after the press.
                            */}
                            <input
                              className="wp-field__input"
                              id={basisCellId}
                              data-testid={basisCellId}
                              type="text"
                              autoComplete="off"
                              aria-label={t('payroll.inputs.grid.cellLabel', {
                                field: t('payroll.inputs.penalty.basis.label'),
                                employee: who,
                              })}
                              aria-invalid={basisProblem !== null}
                              {...(basisProblem === null
                                ? {}
                                : { 'aria-describedby': `${basisCellId}-problem` })}
                              value={basisRaw}
                              onChange={(event) => {
                                props.onGridChange(
                                  setGridCell(
                                    props.grid,
                                    entry.employee_id,
                                    'phat_can_cu',
                                    event.target.value,
                                  ),
                                );
                              }}
                            />
                            {basisProblem === null ? null : (
                              <p
                                className="wp-small"
                                id={`${basisCellId}-problem`}
                                data-testid={`w9-input-problem-${entry.employee_id}-phat_can_cu`}
                              >
                                {/* `reason_too_short` names the minimum; the others take no variable and
                                    ignore it. Passed at both call sites so the two cells cannot
                                    drift apart again (QA-G-023). */}
                                {t(PENALTY_PROBLEM_LABEL[basisProblem], {
                                  min: PENALTY_REASON_MIN,
                                })}
                              </p>
                            )}
                          </td>
                          <td>
                            <input
                              className="wp-field__input"
                              id={reasonCellId}
                              data-testid={reasonCellId}
                              type="text"
                              autoComplete="off"
                              aria-label={t('payroll.inputs.grid.cellLabel', {
                                field: t('payroll.inputs.penalty.reason.label'),
                                employee: who,
                              })}
                              aria-invalid={reasonProblem !== null}
                              {...(reasonProblem === null
                                ? {}
                                : { 'aria-describedby': `${reasonCellId}-problem` })}
                              value={reasonRaw}
                              onChange={(event) => {
                                props.onGridChange(
                                  setGridCell(
                                    props.grid,
                                    entry.employee_id,
                                    'phat_ly_do',
                                    event.target.value,
                                  ),
                                );
                              }}
                            />
                            {reasonProblem === null ? null : (
                              <p
                                className="wp-small"
                                id={`${reasonCellId}-problem`}
                                data-testid={`w9-input-problem-${entry.employee_id}-phat_ly_do`}
                              >
                                {t(PENALTY_PROBLEM_LABEL[reasonProblem], {
                                  min: PENALTY_REASON_MIN,
                                })}
                              </p>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <p className="wp-small" data-testid="w9-inputs-summary">
                {t('payroll.inputs.summary', {
                  cells: summary.enteredCells,
                  people: summary.employeesWithInputs,
                  roster: roster.length,
                })}
              </p>
            </>
          ) : null}
        </>
      ) : null}

      {props.calculateError === null ? null : (
        <ApiErrorNote
          error={props.calculateError}
          subject={t('payroll.inputs.subject')}
          testId="w9-inputs-calculate-error"
        />
      )}
      {calculateReason === null ? null : (
        <p className="wp-small" data-testid="w9-inputs-calculate-reason">
          {t('payroll.inputs.refusedReason', { reason: calculateReason })}
        </p>
      )}
      {capExceeded === null ? null : (
        <p className="wp-small" data-testid="w9-inputs-cap-exceeded">
          {t('payroll.inputs.capExceeded', {
            amount: formatMoney(capExceeded.requestedVnd, currency),
            employee: capExceeded.employeeId,
            pct: String(Math.round((capExceeded.capPct ?? 0) * 100)),
            cap: formatMoney(capExceeded.capVnd, currency),
          })}
        </p>
      )}
      {netPayExceeded === null ? null : (
        <p className="wp-small" data-testid="w9-inputs-net-exceeded">
          {t('payroll.inputs.netPayExceeded', {
            amount: formatMoney(netPayExceeded.requestedVnd, currency),
            employee: netPayExceeded.employeeId,
            net: formatMoney(netPayExceeded.netVnd, currency),
          })}
        </p>
      )}
      {bonusExceeded === null ? null : (
        <p className="wp-small" data-testid="w9-inputs-bonus-exceeded">
          {t('payroll.inputs.bonusExceeded', {
            amount: formatMoney(bonusExceeded.requestedVnd, currency),
            employee: bonusExceeded.employeeId,
            bonus: formatMoney(bonusExceeded.bonusVnd, currency),
          })}
        </p>
      )}
      {calculateIssue === null ? null : (
        <p className="wp-small" data-testid="w9-inputs-issue">
          {calculateIssue.employeeId === null
            ? t('payroll.inputs.issueRefused', {
                field: calculateIssue.field,
                reason: calculateIssue.reason,
              })
            : t('payroll.inputs.issueRefusedFor', {
                employee: calculateIssue.employeeId,
                field: calculateIssue.field,
                reason: calculateIssue.reason,
              })}
        </p>
      )}
      {props.calculatedCount === null ? null : (
        <StatusNote
          severity="low"
          title={t('payroll.inputs.calculated', { count: props.calculatedCount })}
          testId="w9-inputs-calculated"
        />
      )}

      <div className="wp-filters__actions">
        {canCalculate ? (
          <button
            className="wp-button wp-button--primary"
            type="button"
            data-testid="w9-inputs-calculate"
            onClick={calculateNow}
          >
            {t('payroll.inputs.calculate', { count: roster.length })}
          </button>
        ) : null}
        {blockedReason === null ? null : (
          <p className="wp-small" data-testid="w9-inputs-blocked">
            {blockedReason}
          </p>
        )}
        {/* Absent while a write is in flight (R21): closing would sever it from its outcome,
            and clearing would drop the grid a refusal sends the person back to. */}
        {anythingTyped && editable && !writePending ? (
          <button
            className="wp-button"
            type="button"
            data-testid="w9-inputs-clear"
            onClick={() => {
              props.onGridChange({});
              setCsvOutcome(null);
            }}
          >
            {t('payroll.inputs.clear')}
          </button>
        ) : null}
        {writePending ? null : (
          <button
            className="wp-button"
            type="button"
            data-testid="w9-inputs-close"
            onClick={props.onClose}
          >
            {t('payroll.inputs.close')}
          </button>
        )}
      </div>
    </div>
  );
}
